import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const emailRef = useRef();
  const passwordRef = useRef();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Read directly from DOM — fixes mobile autofill bypassing React state
    const form = e.target;
    const email = (
      emailRef.current?.value ||
      form.elements.email?.value ||
      ''
    ).trim().toLowerCase();

    const password = (
      passwordRef.current?.value ||
      form.elements.password?.value ||
      ''
    );

    if (!email || !password) {
      setError('Please enter email and password');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 15000 });
      const data = res.data;

      if (data.role !== 'admin') {
        setError('This account is not an admin account. Please use the patient login.');
        return;
      }

      // Store token and redirect
      try {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.admin));
      } catch {}

      toast.success('Admin login successful!');
      window.location.href = '/admin/dashboard';
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your internet connection and try again.');
      } else if (err.response) {
        setError(err.response.data?.detail || 'Login failed. Please check your credentials.');
      } else {
        setError('Unable to connect to server. Please check your internet and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F4C5C] to-[#0F4C5C]/80" data-testid="admin-login-page">
      <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0F4C5C] rounded-full mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1F2937]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Admin Portal
          </h1>
          <p className="text-[#6B7280] mt-2">Younes Dental Clinic</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-[#1F2937] mb-2">
              Admin Email
            </label>
            <input
              ref={emailRef}
              id="admin-email"
              name="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              inputMode="email"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] transition-colors text-[16px]"
              placeholder="admin@example.com"
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-[#1F2937] mb-2">
              Password
            </label>
            <div className="relative">
              <input
                ref={passwordRef}
                id="admin-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] transition-colors text-[16px]"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1F2937]"
                data-testid="toggle-admin-password"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="admin-login-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-[16px]"
            data-testid="admin-login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In as Admin'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/login" className="text-[#6B7280] hover:text-[#1F2937] text-sm" data-testid="back-to-patient-login">
            Patient Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
